/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Typography } from '@mui/material';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useContext, useEffect, useState } from 'react';

import { StatusCode } from '../../../../../../api/enumerations';
import { updatePublicNotice } from '../../../../../../api/theHive/notices';
import {
  NoticeData,
  UpdatePublicNoticeData,
} from '../../../../../../api/theHive/notices/types';
import { ButtonBox } from '../../../../../../components/UI/Box';
import { GlobalContext } from '../../../../../../context/global';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { TableSection } from '../../NoticeDetails/styles';
import { RichTextMenu } from './RichTextMenu';
import { RichTextReader } from './RichTextReader';
import {
  DialogContainer,
  FilledButton,
  InputBox,
  MessageField,
  OutlinedButton,
  StyledDialog,
} from './styles';

interface DocumentsModalProps {
  noticeData: NoticeData;
  noticeId: number;
}

export function DocumentsModal({
  noticeId,
  noticeData,
}: DocumentsModalProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [rawText, setRawText] = useState('');
  const [richText, setRichText] = useState('');

  const editorEditable = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Digite uma mensagem',
      }),
      CharacterCount.configure({
        limit: 10000,
      }),
      TextStyle,
    ],
    content: '',
    editable: true,
  });

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => {
    setOpen(false);
    if (editorEditable && richText) {
      try {
        const parsedContent = JSON.parse(richText);
        editorEditable.commands.setContent(parsedContent);
      } catch (error) {
        editorEditable.commands.setContent(rawText);
      }
    }
  };

  useEffect(() => {
    setRawText(noticeData.description);
    setRichText(noticeData.description_rich_text);
    if (editorEditable) {
      try {
        const parsedContent = JSON.parse(noticeData.description_rich_text);
        editorEditable.commands.setContent(parsedContent);
      } catch (error) {
        editorEditable.commands.setContent(noticeData.description);
      }
    }
  }, [noticeData]);

  const handleSubmit = async (): Promise<void> => {
    if (!editorEditable) return;

    setLoadingSubmit(true);

    const jsonText = editorEditable.getJSON();
    const editorRawText = editorEditable.getText();
    const editorRichText = editorRawText ? JSON.stringify(jsonText) : '';

    const updateData: UpdatePublicNoticeData = {
      description: editorRawText,
      description_rich_text: editorRichText,
    };

    try {
      const response = await updatePublicNotice(noticeId, updateData);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      setSnackbarMessage('Descrição salva com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);

      setRawText(editorRawText);
      setRichText(editorRichText);
      setOpen(false);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <TableSection>
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize={21}>Descrição do edital</Typography>
        <Button onClick={handleOpen} sx={{ textTransform: 'lowercase' }}>
          {richText ? 'Editar descrição' : 'Adicionar descrição'}
        </Button>
        <StyledDialog open={open} onClose={handleClose}>
          <DialogContainer>
            <Typography fontWeight={600}>
              {richText ? 'Editar descrição' : 'Adicionar descrição'}
            </Typography>
            <MessageField>
              <RichTextMenu editor={editorEditable} />
              <InputBox>
                <EditorContent
                  editor={editorEditable}
                  className="editor-content"
                />
              </InputBox>
            </MessageField>
            <ButtonBox gap={2} my={2.5}>
              <FilledButton onClick={handleSubmit} disabled={loadingSubmit}>
                {richText ? 'editar' : 'enviar'}
              </FilledButton>
              <OutlinedButton onClick={handleClose} disabled={loadingSubmit}>
                cancelar
              </OutlinedButton>
            </ButtonBox>
          </DialogContainer>
        </StyledDialog>
      </Box>
      <Box>
        {richText ? (
          <RichTextReader richText={richText} rawText={rawText} />
        ) : (
          <Typography>Nenhuma descrição foi adicionada</Typography>
        )}
      </Box>
    </TableSection>
  );
}
